import { isFieldCountableAsQuestion } from '@hub/prose-utils';
import { createSelector } from '@reduxjs/toolkit';
import { buildCalculationsDataPreview } from 'features/admin/protocol-editor/logic/calculationsDataPreview';
import type { Section } from '@castoredc/castor_prose';
import type { RootState } from 'features/store/store';

export const adminSelectAvailableQuestionnairesNames = (state: RootState) =>
  state.adminProtocolEditor.availableQuestionnairesNames;
export const adminSelectDestinations = (state: RootState) =>
  state.adminProtocolEditor.destinations;
export const adminSelectQuestionnaireName = (state: RootState) =>
  state.adminProtocolEditor.questionnaireName;
export const adminSelectAvailableSchedulesNames = (state: RootState) =>
  state.adminProtocolEditor.availableSchedulesNames;
export const adminSelectScheduleName = (state: RootState) =>
  state.adminProtocolEditor.scheduleName;
export const adminSelectFieldsDefinitions = (state: RootState) =>
  state.adminProtocolEditor.fieldsDefinitions;
export const adminSelectFieldsData = (state: RootState) =>
  state.adminProtocolEditor.fieldsData;
export const adminSelectCalculationsData = (state: RootState) =>
  state.adminProtocolEditor.calculationsData;
export const adminSelectContextData = (state: RootState) =>
  state.adminProtocolEditor.contextData;
export const adminSelectPages = (state: RootState) =>
  state.adminProtocolEditor.pages;
export const adminSelectPageIndex = (state: RootState) =>
  state.adminProtocolEditor.pageIndex;
export const adminSelectOutputTabIndex = (state: RootState) =>
  state.adminProtocolEditor.outputTabIndex;
export const adminSelectDslSourceError = (state: RootState) =>
  state.adminProtocolEditor.dslSourceError;
export const adminSelectFieldsDataError = (state: RootState) =>
  state.adminProtocolEditor.fieldsDataError;
export const adminSelectContextDataError = (state: RootState) =>
  state.adminProtocolEditor.contextDataError;
export const adminSelectTranslationsError = (state: RootState) =>
  state.adminProtocolEditor.translationsError;
export const adminSelectI18nBaseKey = (state: RootState) =>
  state.adminProtocolEditor.i18nBaseKey;
export const adminSelectScheduleDate = (state: RootState) =>
  state.adminProtocolEditor.scheduleDate;
export const adminSelectScheduleTimezone = (state: RootState) =>
  state.adminProtocolEditor.scheduleTimezone;
export const adminSelectScheduleTasks = (state: RootState) =>
  state.adminProtocolEditor.scheduleTasks;
export const adminSelectRawScheduleOutput = (state: RootState) =>
  state.adminProtocolEditor.rawScheduleOutput;
export const adminSelectScheduleError = (state: RootState) =>
  state.adminProtocolEditor.scheduleError;
export const adminSelectHighlightedFieldName = (state: RootState) =>
  state.adminProtocolEditor.highlightedFieldName;
export const adminSelectDslSource = (state: RootState) =>
  state.adminProtocolEditor.dslSource;
export const adminSelectFieldsDataJson = (state: RootState) =>
  state.adminProtocolEditor.fieldsDataJson;
export const adminSelectContextDataJson = (state: RootState) =>
  state.adminProtocolEditor.contextDataJson;
export const adminSelectStylingCss = (state: RootState) =>
  state.adminProtocolEditor.stylingCss;
export const adminSelectTranslationsJsons = (state: RootState) =>
  state.adminProtocolEditor.translationsJsons;
export const adminSelectLanguagesCodesCsv = (state: RootState) =>
  state.adminProtocolEditor.languagesCodesCsv;
export const adminSelectActiveLanguageCode = (state: RootState) =>
  state.adminProtocolEditor.activeLanguageCode;
export const adminSelectLanguagesCodesError = (state: RootState) =>
  state.adminProtocolEditor.languagesCodesError;

// TODO: Extract common logic with questionnaireSelectors.ts

export const adminSelectCurrentSection = createSelector(
  adminSelectPages,
  adminSelectPageIndex,
  (pages, index): Section | undefined => pages[index]
);

export const adminSelectPagesCount = createSelector(
  adminSelectPages,
  pages => pages.length
);

export const adminSelectTotalQuestionsCount = createSelector(
  adminSelectFieldsDefinitions,
  definitions => definitions.filter(d => isFieldCountableAsQuestion(d)).length
);

export const adminSelectQuestionsCountOnPreviousPages = createSelector(
  adminSelectFieldsDefinitions,
  adminSelectPages,
  adminSelectPageIndex,
  (definitions, pages, index) => {
    const previousPages = pages.slice(0, index);
    return previousPages.reduce(
      (sum, page) =>
        sum +
        definitions.filter(
          d => isFieldCountableAsQuestion(d) && page.fields.includes(d.name)
        ).length,
      0
    );
  }
);

export const adminSelectPageFieldsDefinitions = createSelector(
  adminSelectFieldsDefinitions,
  adminSelectPages,
  adminSelectPageIndex,
  (definitions, pages, index) => {
    const page = pages[index];
    return page ? definitions.filter(f => page.fields.includes(f.name)) : [];
  }
);

export const adminSelectCalculationsValuesPreview = createSelector(
  adminSelectCalculationsData,
  calculationsData => {
    return buildCalculationsDataPreview(calculationsData);
  }
);

export const adminSelectActiveTranslationJson = createSelector(
  [adminSelectTranslationsJsons, adminSelectActiveLanguageCode],
  (allTranslations, activeCode) => allTranslations[activeCode] ?? '{}'
);

export const adminSelectLanguagesCodes = createSelector(
  [adminSelectLanguagesCodesCsv, adminSelectLanguagesCodesError],
  (languageCodes, error) => {
    if (error) return [];
    const trimmedValue = languageCodes.trim();
    if (!trimmedValue) return [];
    return trimmedValue
      .split(',')
      .map(code => code.trim())
      .filter(code => code.length > 0);
  }
);

export const adminSelectBrand = (state: RootState) =>
  state.adminProtocolEditor.brand;
